/**
 * My Interviews — interviewer-facing dashboard.
 *
 * Shows the caller's assigned interview rounds split into:
 *  - Pending feedback (must be submitted; round not cancelled)
 *  - Submitted (history, blind-mode unwound after all peers have submitted)
 *
 * Clicking a row opens the ScorecardSubmit dialog with the frozen question
 * set from the round's template snapshot. Submitting locks the row.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Star, Clock, MapPin, VideoCamera, Phone, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const REC_OPTIONS = [
  { value: "strong_hire",    label: "Strong Hire",    color: "bg-emerald-600 text-white" },
  { value: "hire",           label: "Hire",           color: "bg-emerald-100 text-emerald-800" },
  { value: "no_hire",        label: "No Hire",        color: "bg-rose-100 text-rose-800" },
  { value: "strong_no_hire", label: "Strong No Hire", color: "bg-rose-600 text-white" },
];

const errMsg = (e) => formatApiError(e?.response?.data?.detail) || "Something went wrong";

function fmtDT(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function MyInterviews() {
  const [pending, setPending] = useState([]);
  const [submitted, setSubmitted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [activeIv, setActiveIv] = useState(null); // round id being scored

  const load = async () => {
    setLoading(true);
    try {
      const [p, all] = await Promise.all([
        api.get("/interview-loop/my-pending"),
        api.get("/interview-loop/rounds?mine=true"),
      ]);
      setPending(p.data.pending || []);
      const pendingIds = new Set((p.data.pending || []).map(r => r.id));
      setSubmitted((all.data || []).filter(r => !pendingIds.has(r.id)));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const focus = params.get("focus");
    if (focus) setActiveIv(focus);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmitted = () => {
    setActiveIv(null);
    params.delete("focus");
    setParams(params, { replace: true });
    load();
    toast.success("Scorecard submitted");
  };

  if (loading) {
    return <AppShell><div className="p-8 text-zinc-500">Loading…</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="my-interviews-page">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">My Interviews</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Scorecards you owe and the ones you've submitted. Scorecards are private until every interviewer in the round has submitted.
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-semibold" data-testid="my-pending-count">{pending.length}</div>
            <div className="text-xs uppercase tracking-wide text-zinc-500">Pending feedback</div>
          </div>
        </header>

        <Card className="p-6" data-testid="pending-card">
          <h2 className="text-base font-semibold mb-3">Pending feedback ({pending.length})</h2>
          {pending.length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center border border-dashed rounded" data-testid="no-pending">
              You're all caught up — nothing to score right now.
            </div>
          ) : (
            <div className="divide-y" data-testid="pending-list">
              {pending.map(r => (
                <InterviewRow key={r.id} r={r} pending onClick={() => setActiveIv(r.id)} />
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6" data-testid="history-card">
          <h2 className="text-base font-semibold mb-3">Submitted ({submitted.length})</h2>
          {submitted.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">No submitted scorecards yet.</div>
          ) : (
            <div className="divide-y">
              {submitted.map(r => (
                <InterviewRow key={r.id} r={r} onClick={() => setActiveIv(r.id)} />
              ))}
            </div>
          )}
        </Card>
      </div>

      {activeIv && (
        <ScorecardDialog
          interviewId={activeIv}
          onClose={() => setActiveIv(null)}
          onSubmitted={onSubmitted}
        />
      )}
    </AppShell>
  );
}

function ModeIcon({ mode }) {
  if (mode === "video") return <VideoCamera size={14} />;
  if (mode === "in_person") return <MapPin size={14} />;
  if (mode === "phone") return <Phone size={14} />;
  return <Clock size={14} />;
}

function InterviewRow({ r, pending, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left py-3 hover:bg-zinc-50 transition px-2 -mx-2 rounded"
      data-testid={`interview-row-${r.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium">{r.candidate_name || "Candidate"}</div>
            <Badge variant="outline" className="text-[10px]">{r.round_name}</Badge>
            {r.status === "completed" && <Badge className="text-[10px] bg-emerald-100 text-emerald-800">Complete</Badge>}
            {r.status === "cancelled" && <Badge className="text-[10px] bg-zinc-200 text-zinc-700">Cancelled</Badge>}
          </div>
          <div className="text-xs text-zinc-500 mt-1">{r.requisition_title}</div>
          <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3">
            <span className="flex items-center gap-1"><Clock size={12}/> {fmtDT(r.scheduled_at)}</span>
            <span className="flex items-center gap-1"><ModeIcon mode={r.mode}/> {(r.mode || "").replace("_", " ")}</span>
            <span>{r.scorecards_submitted_count}/{r.scorecards_required_count} scorecards in</span>
          </div>
        </div>
        <div className="shrink-0">
          {pending ? (
            <Badge className="bg-amber-100 text-amber-800">Action required</Badge>
          ) : (
            <CheckCircle size={20} weight="fill" className="text-emerald-500"/>
          )}
        </div>
      </div>
    </button>
  );
}

function ScorecardDialog({ interviewId, onClose, onSubmitted }) {
  const [rnd, setRnd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [recommendation, setRecommendation] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/interview-loop/rounds/${interviewId}`)
      .then(({ data }) => {
        setRnd(data);
        // Hydrate from existing scorecard if already submitted
        if (data.my_scorecard) {
          setAnswers(data.my_scorecard.answers || {});
          setRecommendation(data.my_scorecard.overall_recommendation || "");
          setNotes(data.my_scorecard.notes || "");
        }
      })
      .catch(e => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [interviewId]);

  const tpl = rnd?.scorecard_template_snapshot;
  const questions = tpl?.questions || [];
  const alreadySubmitted = !!rnd?.my_scorecard;
  const showPeers = rnd?.can_see_aggregate && (rnd.scorecards || []).length > 0;

  const setAnswer = (qid, value) => setAnswers(a => ({ ...a, [qid]: value }));

  const submit = async () => {
    // Validate required Qs
    for (const q of questions) {
      if (!q.required) continue;
      const v = answers[q.id];
      if (q.type === "recommendation") {
        if (!recommendation) { toast.error(`Pick an overall recommendation`); return; }
        continue;
      }
      if (v === undefined || v === "" || (Array.isArray(v) && !v.length)) {
        toast.error(`"${q.label}" is required`); return;
      }
    }
    if (!recommendation) { toast.error("Pick an overall recommendation"); return; }
    setSubmitting(true);
    try {
      const payloadAnswers = { ...answers, recommendation };
      await api.post(`/interview-loop/rounds/${interviewId}/scorecard`, {
        answers: payloadAnswers,
        overall_recommendation: recommendation,
        notes: notes || null,
      });
      onSubmitted();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="scorecard-dialog">
        <DialogHeader>
          <DialogTitle>
            {loading ? "Loading…" : `Scorecard — ${rnd?.candidate_name} · ${rnd?.round_name}`}
          </DialogTitle>
          <DialogDescription>
            {rnd?.requisition_title} · {fmtDT(rnd?.scheduled_at || "")}
            {alreadySubmitted && " · You've already submitted — you can update below."}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-sm text-zinc-500">Loading…</div>
        ) : (
          <div className="space-y-4">
            {questions.filter(q => q.type !== "recommendation").map((q) => (
              <QuestionField key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
            ))}

            <div className="border border-zinc-200 rounded p-4 bg-amber-50">
              <Label className="text-sm font-medium block mb-2">Overall recommendation *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {REC_OPTIONS.map(o => (
                  <button
                    key={o.value}
                    onClick={() => setRecommendation(o.value)}
                    className={`px-3 py-2 rounded text-sm border ${recommendation === o.value ? o.color + " border-transparent" : "bg-white border-zinc-300 hover:bg-zinc-50"}`}
                    data-testid={`rec-${o.value}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything else you want to add, freeform." data-testid="sc-notes" />
            </div>

            {!showPeers && (
              <div className="text-xs text-zinc-500 italic px-1">
                Peer scorecards are hidden until every interviewer in this round has submitted (no anchoring bias).
              </div>
            )}

            {showPeers && (
              <div className="border border-zinc-200 rounded p-4 bg-zinc-50" data-testid="peer-scorecards">
                <div className="text-sm font-medium mb-2">Peer scorecards ({(rnd.scorecards || []).length})</div>
                <div className="space-y-2">
                  {rnd.scorecards.map(sc => {
                    const rec = REC_OPTIONS.find(o => o.value === sc.overall_recommendation);
                    return (
                      <div key={sc.id} className="text-xs border-l-2 border-zinc-300 pl-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{sc.interviewer_name}</span>
                          {rec && <span className={`text-[10px] px-1.5 py-0.5 rounded ${rec.color}`}>{rec.label}</span>}
                        </div>
                        {sc.notes && <div className="text-zinc-600 mt-0.5">{sc.notes}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Close</Button>
          {!loading && rnd?.status !== "cancelled" && (
            <Button onClick={submit} disabled={submitting} data-testid="sc-submit">
              {submitting ? "Submitting…" : (alreadySubmitted ? "Update scorecard" : "Submit scorecard")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuestionField({ q, value, onChange }) {
  if (q.type === "rating_5") {
    return (
      <div data-testid={`q-${q.id}`}>
        <Label className="text-sm">{q.label}{q.required ? " *" : ""}</Label>
        <div className="flex gap-1 mt-1">
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              onClick={() => onChange(n)}
              className={`p-1 ${value >= n ? "text-amber-500" : "text-zinc-300"} hover:text-amber-400`}
              data-testid={`q-${q.id}-${n}`}
            >
              <Star size={22} weight={value >= n ? "fill" : "regular"} />
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (q.type === "yes_no") {
    return (
      <div data-testid={`q-${q.id}`}>
        <Label className="text-sm">{q.label}{q.required ? " *" : ""}</Label>
        <div className="flex gap-2 mt-1">
          {["Yes", "No"].map(o => (
            <button
              key={o}
              onClick={() => onChange(o)}
              className={`px-4 py-1.5 rounded border text-sm ${value === o ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 hover:bg-zinc-50"}`}
              data-testid={`q-${q.id}-${o}`}
            >{o}</button>
          ))}
        </div>
      </div>
    );
  }
  if (q.type === "single_select") {
    return (
      <div data-testid={`q-${q.id}`}>
        <Label className="text-sm">{q.label}{q.required ? " *" : ""}</Label>
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full mt-1 px-3 py-2 border border-zinc-300 rounded text-sm"
        >
          <option value="">— Choose —</option>
          {(q.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (q.type === "multi_select") {
    const cur = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(cur.includes(o) ? cur.filter(x => x !== o) : [...cur, o]);
    return (
      <div data-testid={`q-${q.id}`}>
        <Label className="text-sm">{q.label}{q.required ? " *" : ""}</Label>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {(q.options || []).map(o => (
            <button
              key={o}
              onClick={() => toggle(o)}
              className={`px-2.5 py-1 text-xs rounded border ${cur.includes(o) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 hover:bg-zinc-50"}`}
            >{o}</button>
          ))}
        </div>
      </div>
    );
  }
  // text
  return (
    <div data-testid={`q-${q.id}`}>
      <Label className="text-sm">{q.label}{q.required ? " *" : ""}</Label>
      <Textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Type your response…" />
    </div>
  );
}
